/**
 * Costs API Client
 *
 * Handles all cost tracking and budget management API calls
 * Uses centralized apiClient for consistent API access
 *
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Re-export response types for consumers
export type { TimComboAPIResponse };

/**
 * Cost Summary interface
 */
export interface CostSummary {
  Cost_Summary_ID: string;
  Client_ID: string;
  Company_ID?: string;
  Project_ID?: string;
  Period_Start: string;
  Period_End: string;
  Total_Cost: number;
  Budget_Allocated: number;
  Utilization_Percentage: number;
  Execution_Count: number;
  Agent_Count?: number;
  Model_Distribution?: ModelCostBreakdown[];
  Trend?: {
    Previous_Period_Cost: number;
    Change_Percentage: number;
    Direction: 'up' | 'down' | 'stable';
  };
  Status: 'under_budget' | 'at_budget' | 'over_budget';
  Create_Date: string;
  Last_Updated: string;
}

/**
 * Model cost breakdown
 */
export interface ModelCostBreakdown {
  Model_Name: string;
  Model_Type: 'opus' | 'sonnet' | 'haiku';
  Execution_Count: number;
  Total_Cost: number;
  Average_Cost_Per_Execution: number;
  Input_Tokens: number;
  Output_Tokens: number;
  Percentage_Of_Total: number;
}

/**
 * Project cost details
 */
export interface ProjectCost {
  Project_ID: string;
  Project_Name: string;
  Client_ID: string;
  Company_ID: string;
  Total_Cost: number;
  Budget_Allocated: number;
  Utilization_Percentage: number;
  Execution_Count: number;
  Period_Start: string;
  Period_End: string;
  Status: 'under_budget' | 'at_budget' | 'over_budget';
  Model_Distribution?: ModelCostBreakdown[];
}

/**
 * Company cost details
 */
export interface CompanyCost {
  Company_ID: string;
  Company_Name: string;
  Client_ID: string;
  Total_Cost: number;
  Budget_Allocated: number;
  Utilization_Percentage: number;
  Project_Count: number;
  Execution_Count: number;
  Period_Start: string;
  Period_End: string;
  Status: 'under_budget' | 'at_budget' | 'over_budget';
  Top_Projects?: ProjectCost[];
}

/**
 * Client cost details
 */
export interface ClientCost {
  Client_ID: string;
  Client_Name: string;
  Total_Cost: number;
  Budget_Allocated: number;
  Utilization_Percentage: number;
  Company_Count: number;
  Project_Count: number;
  Execution_Count: number;
  Period_Start: string;
  Period_End: string;
  Status: 'under_budget' | 'at_budget' | 'over_budget';
  Top_Companies?: CompanyCost[];
}

/**
 * Cost query parameters
 */
export interface CostQueryParams {
  period_start?: string;
  period_end?: string;
  include_breakdown?: boolean;
  include_trends?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Time period presets
 */
export type TimePeriod = 'this_month' | 'last_month' | 'last_quarter' | 'last_year' | 'custom';

/**
 * Export format types
 */
export type ExportFormat = 'csv' | 'json' | 'xlsx';

/**
 * Get project costs
 * GET /api/costs/projects
 */
export async function getProjectCosts(
  clientId: string,
  companyId?: string,
  projectId?: string,
  params?: CostQueryParams
): Promise<TimComboAPIResponse<ProjectCost>> {
  const queryParams: Record<string, string> = { Client_ID: clientId };
  if (companyId) queryParams.Company_ID = companyId;
  if (projectId) queryParams.Project_ID = projectId;
  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;
  if (params?.include_breakdown !== undefined) queryParams.include_breakdown = params.include_breakdown.toString();
  if (params?.include_trends !== undefined) queryParams.include_trends = params.include_trends.toString();

  return Make_Authorized_API_Call<ProjectCost>(
    API_ENDPOINTS.COSTS.PROJECTS,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Get company costs
 * GET /api/costs/companies
 */
export async function getCompanyCosts(
  clientId: string,
  companyId?: string,
  params?: CostQueryParams
): Promise<TimComboAPIResponse<CompanyCost>> {
  const queryParams: Record<string, string> = { Client_ID: clientId };
  if (companyId) queryParams.Company_ID = companyId;
  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;
  if (params?.include_breakdown !== undefined) queryParams.include_breakdown = params.include_breakdown.toString();
  if (params?.include_trends !== undefined) queryParams.include_trends = params.include_trends.toString();

  return Make_Authorized_API_Call<CompanyCost>(
    API_ENDPOINTS.COSTS.COMPANIES,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Get client costs
 * GET /api/costs/clients
 */
export async function getClientCosts(
  clientId: string,
  params?: CostQueryParams
): Promise<TimComboAPIResponse<ClientCost>> {
  const queryParams: Record<string, string> = { Client_ID: clientId };
  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;
  if (params?.include_breakdown !== undefined) queryParams.include_breakdown = params.include_breakdown.toString();
  if (params?.include_trends !== undefined) queryParams.include_trends = params.include_trends.toString();

  return Make_Authorized_API_Call<ClientCost>(
    API_ENDPOINTS.COSTS.CLIENTS,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Get cost summary for any level
 * GET /api/costs/summary
 */
export async function getCostSummary(
  clientId: string,
  companyId?: string,
  projectId?: string,
  params?: CostQueryParams
): Promise<TimComboAPIResponse<CostSummary>> {
  const queryParams: Record<string, string> = { Client_ID: clientId };
  if (companyId) queryParams.Company_ID = companyId;
  if (projectId) queryParams.Project_ID = projectId;
  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;
  if (params?.include_breakdown !== undefined) queryParams.include_breakdown = params.include_breakdown.toString();
  if (params?.include_trends !== undefined) queryParams.include_trends = params.include_trends.toString();

  // Note: Cost summary endpoint may need to be added to API_ENDPOINTS
  return Make_Authorized_API_Call<CostSummary>(
    `${API_ENDPOINTS.COSTS.CLIENTS.replace('/clients', '/summary')}`,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Export cost data
 * GET /api/costs/export
 */
export async function exportCostData(
  clientId: string,
  format: ExportFormat = 'csv',
  params?: CostQueryParams
): Promise<Blob> {
  const queryParams: Record<string, string> = { Client_ID: clientId, format };
  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;

  // For blob responses, we need to use fetch directly
  const token = localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('No access token available');
  }

  const url = new URL(`${API_ENDPOINTS.COSTS.CLIENTS.replace('/clients', '/export')}`);
  Object.entries(queryParams).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }

  return await response.blob();
}

// =============================================================================
// UTILITY FUNCTIONS (no API calls)
// =============================================================================

/**
 * Helper to get date range for time period presets
 */
export function getDateRangeForPeriod(period: TimePeriod, customStart?: string, customEnd?: string): { start: string; end: string } {
  const now = new Date();
  let start: Date;
  let end: Date = now;

  switch (period) {
    case 'this_month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'last_month':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case 'last_quarter':
      const currentQuarter = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
      end = new Date(now.getFullYear(), currentQuarter * 3, 0);
      break;
    case 'last_year':
      start = new Date(now.getFullYear() - 1, 0, 1);
      end = new Date(now.getFullYear() - 1, 11, 31);
      break;
    case 'custom':
      if (!customStart || !customEnd) {
        throw new Error('Custom date range requires start and end dates');
      }
      return {
        start: customStart,
        end: customEnd
      };
    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
}

/**
 * Helper to format currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Helper to get budget status color
 */
export function getBudgetStatusColor(status: 'under_budget' | 'at_budget' | 'over_budget'): string {
  switch (status) {
    case 'under_budget':
      return 'green';
    case 'at_budget':
      return 'yellow';
    case 'over_budget':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Helper to get utilization color based on percentage
 */
export function getUtilizationColor(utilizationPct: number): string {
  if (utilizationPct < 80) return 'green';
  if (utilizationPct <= 100) return 'yellow';
  return 'red';
}
